import { Button, Card, Col, Input, Row, Switch, Typography } from 'antd';
import { StyleSheet } from "../../../StyleSheet";
import { useCallback, useEffect, useState } from 'react';
import { CustomizationWifiItem, RequestUpdateWifi } from '../../../types/common';
import { images } from "../../../routes/ImageRoutes"
import { EyeFilled, EyeInvisibleFilled } from '@ant-design/icons';
import { updateWifi } from '../../../methods/Methods';
import { AlertSliceInterface, clearAlert, setAlert, showAlert } from '../../../stores/slices/alertSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../stores/store';
import InfoCardComponent from '../../../components/InfoCardComponent';

const { Text } = Typography;

interface Props {
    idCliente: string;
    data: CustomizationWifiItem;
    attempts?: number,
    onUpdated?: () => void;
    setLoading: (value: boolean) => void;
    serviceEnable?: boolean;
};

const WifiUpdate = (props: Props) => {
    const { data, attempts, idCliente, onUpdated, setLoading, serviceEnable } = props;
    const [toEnabled, setToEnabled] = useState(false);
    const [nameRed, setNameRed] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const dispatch = useDispatch<AppDispatch>();

    const onChange = async (checked: boolean) => {
        setToEnabled(checked);
        let typeAlert = "";
        let messageAlert = "";
        let requestBodyUpdateEnabled: RequestUpdateWifi = {
            attempts: attempts !== undefined ? attempts : 0,
            isUpdateEnabled: true,
            data: [
                {
                    Tipo_Wifi: data.Tipo_Wifi,
                    Id_suscriptor: data.Id_suscriptor,
                    Id_sistema: data.Id_sistema,
                    estado_modem: data.estado_modem,
                    Parametros: [
                        {
                            nombre: "Enable",
                            valor: checked === true ? true : false
                        }
                    ]
                }
            ]
        }

        await updateWifi(idCliente, requestBodyUpdateEnabled)
            .then((response) => {
                if (response.data.data[0] && response.data.data[0].status === true) {
                    typeAlert = "SUCCESS";
                    onUpdated?.();
                } else {
                    typeAlert = "ERROR";
                }
                messageAlert = response.data.data[0].message;

                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: typeAlert as "INFO" | "SUCCESS" | "ERROR",
                    title: "¡Estimado Usuario!",
                    message: messageAlert,
                    onFinalize: () => handleDispatchAlertModal()
                }
                openAlertModal(dispatchAlert);
            })
            .catch(() => {
                typeAlert = "ERROR";
                messageAlert = "Hubo un error al procesar su solicitud. \nIntente más tarde.";
                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: typeAlert as "INFO" | "SUCCESS" | "ERROR",
                    title: "¡Estimado Usuario!",
                    message: messageAlert,
                    onFinalize: () => handleDispatchAlertModal()
                }
                openAlertModal(dispatchAlert);
            })
    };

    useEffect(() => {
        if (data.Parametros.length > 0) {
            const ssidParam = data.Parametros.find(p => p.nombre === "SSID")?.valor;
            setNameRed(ssidParam as string);
            const enableParam = data.Parametros.find(p => p.nombre === "Enable")?.valor;
            setToEnabled(enableParam === true);
        }
    }, [data]);


    const handleDispatchAlertModal = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
    }, [dispatch]);

    const openAlertModal = useCallback((option: AlertSliceInterface) => {
        dispatch(setAlert({
            show: option.show,
            type: option.type as "INFO" | "SUCCESS" | "ERROR",
            title: option.title,
            message: option.message,
            onFinalize: option.onFinalize
        }));
    }, [dispatch]);

    const modifyNetwork = async () => {
        setLoading(true);
        let typeAlert = "";
        let messageAlert = "";

        const originalSSID = data.Parametros.find(p => p.nombre === "SSID")?.valor ?? "";

        const hasSSIDChanged = nameRed !== originalSSID;
        const hasPassword = password.trim() !== "";

        if (!hasSSIDChanged && !hasPassword) {
            typeAlert = "INFO";

            messageAlert = "No se detectaron cambios en la red Wi-Fi.";

            let dispatchAlert: AlertSliceInterface = {
                show: true,
                type: typeAlert as "INFO" | "SUCCESS" | "ERROR",
                title: "¡Estimado Usuario!",
                message: messageAlert,
                onFinalize: () => handleDispatchAlertModal()
            }
            openAlertModal(dispatchAlert);
            setLoading(false);
            return;
        }

        let requestBodyUpdate: RequestUpdateWifi = {
            attempts: attempts !== undefined ? attempts : 0,
            isUpdateEnabled: false,
            data: [
                {
                    Tipo_Wifi: data.Tipo_Wifi,
                    Id_suscriptor: data.Id_suscriptor,
                    Id_sistema: data.Id_sistema,
                    estado_modem: data.estado_modem,
                    Parametros: [
                        {
                            nombre: "SSID",
                            valor: nameRed
                        },
                        ...(password ? [{
                            nombre: "PreSharedKey",
                            valor: password
                        }] : [])
                    ]
                }
            ]
        }

        if (hasSSIDChanged || hasPassword) {
            await updateWifi(idCliente, requestBodyUpdate)
                .then((response) => {
                    if (response.data.data[0] && response.data.data[0].status === true) {
                        typeAlert = "SUCCESS";
                        onUpdated?.();
                    } else {
                        typeAlert = "ERROR";
                    }
                    messageAlert = response.data.data[0].message;

                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: typeAlert as "INFO" | "SUCCESS" | "ERROR",
                        title: "¡Estimado Usuario!",
                        message: messageAlert,
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchAlert);
                    setLoading(false);
                })
                .catch(() => {
                    typeAlert = "ERROR";
                    messageAlert = "Hubo un error al procesar su solicitud. \nIntente más tarde.";
                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: typeAlert as "INFO" | "SUCCESS" | "ERROR",
                        title: "¡Estimado Usuario!",
                        message: messageAlert,
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchAlert);
                    setLoading(false);
                })
        }
    }

    return (
        <Row style={styles.rowRoot}>
            {serviceEnable ? (
                <Card bodyStyle={styles.bodyCard} style={{ ...styles.cardRoot, borderRadius: 10 }} >
                    <Row style={styles.rowCardWifi}>
                        <Col style={{ width: '10%', justifyContent: 'center', display: 'flex', paddingTop: 10, paddingBottom: 10 }}>
                            {
                                toEnabled ? (
                                    <img style={styles.logoIcon} src={images.iconCheckLevels} alt={""} />
                                ) : (
                                    <img style={styles.logoIcon} src={images.wifiOff} alt={""} />
                                )
                            }
                        </Col>
                        <Col style={styles.colTextWifi}>
                            <Text style={styles.textBold}>
                                {`Wi-Fi ${toEnabled ? "Encendido" : "Apagado"}`}
                            </Text>
                        </Col>
                        <Col style={styles.colSwitch}>
                            <Switch checked={toEnabled} onChange={onChange} disabled={attempts !== undefined && attempts <= 0} />
                        </Col>
                    </Row>
                </Card>
            ) : null}

            {
                attempts !== undefined && attempts <= 0 ? (
                    <Row style={styles.rowPrincipal}>
                        <InfoCardComponent
                            title="¡Estimado Usuario!"
                            description="Ha alcanzado el límite diario de actualizaciones para su red Wi-Fi."
                            color="#fce5b5"
                            colorText="#c28a0f"
                        />
                    </Row>
                ) : (
                    <>
                        <Row style={styles.rowInputs}>
                            <Col style={{ width: '45%' }}>
                                <Text style={styles.inputTitle}>Nombre de la red</Text>
                                <Input
                                    value={nameRed}
                                    style={styles.inputText}
                                    onChange={(e) => setNameRed(e.target.value)}
                                />
                            </Col>
                            <Col style={{ width: '10%' }} />
                            <Col style={{ width: '45%' }}>
                                <Text style={styles.inputTitle}>Contraseña</Text>
                                <Input.Password
                                    iconRender={(visible) => (visible ? <EyeFilled style={{ fontSize: 20, color: '#1677ff' }} /> : <EyeInvisibleFilled style={{ fontSize: 20, color: '#1677ff' }} />)}
                                    value={password}
                                    style={styles.inputText}
                                    placeholder='Contraseña'
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </Col>
                        </Row>
                        <Row style={styles.rowButtons}>
                            <Button style={styles.buttonFinal} type="primary" onClick={() => modifyNetwork()} disabled={attempts !== undefined && attempts <= 0}>
                                Guardar
                            </Button>
                        </Row>
                    </>
                )
            }

        </Row>
    );
};

const styles = StyleSheet.create({
    rowPrincipal: {
        width: '93%',
        padding: 10,
        flexDirection: 'row',
        display: 'flex',
        alignItems: 'center'
    },
    colTextError: {
        width: '100%',
        flexDirection: 'column'
    },
    text: {
        fontSize: 15,
        textAlign: 'left',
        maxWidth: "100%"
    },

    rowRoot: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 10,
        width: '100%'
    },
    bodyCard: {
        justifyContent: 'center',
        padding: 5
    },
    cardRoot: {
        backgroundColor: 'white',
        marginBottom: 10,
        boxShadow: "0px 0px 8px #00000029",
        borderRadius: 20,
        // marginTop: 10,
        width: '90%'
    },
    rowCardWifi: {
        display: 'flex',
        flexDirection: 'row'
    },
    logoIcon: {
        // resizeMode: 'contain',
        width: 30,
        height: 30,
        // paddingLeft: 15
    },
    textBold: {
        fontSize: 15,
        color: "#000000",
        textAlign: 'left',
        fontWeight: 600
    },
    colTextWifi: {
        width: '75%',
        display: 'flex',
        justifyContent: "flex-start",
        alignItems: 'center',
        paddingLeft: 10
    },
    colSwitch: {
        width: '15%',
        display: 'flex',
        justifyContent: "flex-end",
        alignItems: 'center',
        paddingRight: 10
    },
    rowInputs: {
        marginTop: 10,
        width: '90%',
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 20
    },
    inputTitle: {
        color: "#0055c2",
        fontWeight: 600,
        fontSize: 12
    },
    inputText: {
        marginTop: 15,
        color: "#000000",
        borderColor: '#000000',
        fontSize: 12
    },
    rowButtons: {
        marginTop: 10,
        marginBottom: 20,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'nowrap',
        width: '90%'
    },
    buttonFinal: {
        backgroundColor: "#0055c2",
        borderRadius: 15,
        color: "white",
        boxShadow: "3px 3px 5px 0px rgba(0,0,0,0.10)",
        marginBottom: 10,
        width: 140,
    },
});

export default WifiUpdate;
